<script setup lang="ts">

import { LINKS } from '@presentation/Types/Settings';

import Battery from '@presentation/Icons/Battery.vue';
import Brakes from '@presentation/Icons/Brakes.vue';
import Concierge from '@presentation/Icons/Concierge.vue';
import KeyFob from '@presentation/Icons/KeyFob.vue';
import Tyres from '@presentation/Icons/Tyres.vue';
import WiperBlades from '@presentation/Icons/WiperBlades.vue';

type Props = { showConditions?: boolean }
const { showConditions = false } = defineProps<Props>();

</script>

<template>
    <div class="flex flex-wrap leading-tight"
        :class="showConditions ? 'gap-y-0' : 'gap-y-4 lg:gap-y-8'">
        
        <div class="flex flex-col items-center basis-1/2 text-center py-2 2xl:px-4">
            <WiperBlades class="w-10 2xl:w-14 text-[#B5424A]"/>
            <h1 class="font-bold 2xl:my-1 text-sm md:text-base 3xl:text-lg 4xl:text-2xl">Wiper Blade Replacement</h1>
            <h2 class="text-white/60 text-xs md:text-sm 3xl:text-base 4xl:text-lg">
                <div v-if="showConditions" class="leading-tight">
                    Eligible for <span class="text-white font-bold">1</span> replacement set
                    <div class="text-violet-400" style="font-size:0.9em">Including rear wiper</div>
                </div>
                <div v-else class="leading-tight">Replace worn front and rear wipers</div>
            </h2>
        </div>

        <div class="flex flex-col items-center basis-1/2 text-center py-2 2xl:px-4">
            <Battery class="w-10 2xl:w-14 text-[#B5424A]" />
            <h1 class="font-bold 2xl:my-1 text-sm md:text-base 3xl:text-lg 4xl:text-2xl">12V Battery Replacement</h1>
            <h2 class="text-white/60 text-xs md:text-sm 3xl:text-base 4xl:text-lg">
                <div v-if="showConditions" class="leading-tight">
                    Eligible for <span class="text-white font-bold">1</span> replacement
                    <div class="text-violet-400" style="font-size:0.9em">If car can't be started</div>
                </div>
                <div v-else class="leading-tight">Replace battery if it fails from normal wear</div>
            </h2>
        </div>

        <div class="flex flex-col items-center basis-1/2 text-center py-2 2xl:px-4">
            <Brakes class="w-10 2xl:w-14 text-[#B5424A]"/>
            <h1 class="font-bold 2xl:my-1 text-sm md:text-base 3xl:text-lg 4xl:text-2xl">Brake Safety</h1>
            <h2 class="text-white/60 text-xs md:text-sm 3xl:text-base 4xl:text-lg">
                <div v-if="showConditions" class="leading-tight">
                    Eligible for <span class="text-white font-bold">2</span> replacements
                    <div class="text-violet-400" style="font-size:0.9em">Front or back count as 1 <span class="hidden 2xl:inline">replacement</span></div>
                </div>
                <div v-else class="leading-tight">Replace worn brake pads, rotors, or drums</div>
            </h2>
        </div>

        <div class="flex flex-col items-center basis-1/2 text-center py-2 2xl:px-4">
            <KeyFob class="w-10 2xl:w-14 text-[#B5424A]" />
            <h1 class="font-bold 2xl:my-1 text-sm md:text-base 3xl:text-lg 4xl:text-2xl">Key Fob Repair</h1>
            <h2 class="text-white/60 text-xs md:text-sm 3xl:text-base 4xl:text-lg">
                <div v-if="showConditions" class="leading-tight">
                    Eligible for <span class="text-white font-bold">4</span> repairs or replacements
                    <div class="text-violet-400" style="font-size:0.9em">Includes recoding if needed</div>
                </div>
                <div v-else class="leading-tight">Repair or replace battery, buttons, casing, or recode</div>
            </h2>
        </div>

        <div class="flex flex-col items-center basis-1/2 text-center py-2 2xl:px-4">
            <Tyres class="w-10 2xl:w-14 text-[#B5424A]" />
            <h1 class="font-bold 2xl:my-1 text-sm md:text-base 3xl:text-lg 4xl:text-2xl">Tyre Safety</h1>
            <h2 class="text-white/60 text-xs md:text-sm 3xl:text-base 4xl:text-lg">
                <div v-if="showConditions" class="leading-tight">
                    Eligible for <span class="text-white font-bold">6</span> repairs/<span class="text-white font-bold">4</span> replacements
                    <div class="text-violet-400" style="font-size:0.9em">Each tyre counts as 1 <span class="hidden 3xl:inline">repair/replacement</span></div>
                </div>
                <div v-else class="leading-tight">Protection against punctures, tears, and road hazard damage, including replacement if needed</div>
            </h2>
        </div>

        <div class="flex flex-col items-center basis-1/2 text-center py-2 2xl:px-4">
            <Concierge class="w-10 2xl:w-14 -mb-2 -top-2 text-[#B5424A]" />
            <h1 class="font-bold 2xl:my-1 text-sm md:text-base 3xl:text-lg 4xl:text-2xl">Concierge Service</h1>
            <h2 class="text-white/60 text-xs md:text-sm 3xl:text-base 4xl:text-lg">
                <div class="leading-tight">We handle everything for you. Claims, service bookings, and support</div>
            </h2>
        </div>

        <div v-if="showConditions"
             class="flex flex-col justify-center text-center text-xs 2xl:text-base 3xl:text-lg
                    text-white/80 2xl:mt-6">
            Applies to normal wear &amp; tear only, and excludes damage caused by accidents, road hazards and negligence.
            <a :href="LINKS.DealerTermsAndConditions" target="_blank" class="mx-auto flex items-center mt-2 text-cyan-500 font-semibold">
                See full terms and conditions 
                <Icon name="chevron-right" />
            </a>
        </div>

    </div>
    
</template>